import { parse } from "date-fns";
import { DateTime } from "luxon";

import { FREQUENCIES } from "../../../../Recurrence";

import type { ACHDeliverySpeed, LimitsForDisplayType } from "./ACHPayment.form";

type RecurrenceRuleValues = {
  isVisible: boolean;
  ends: "" | "never" | "on" | "after";
  frequency?: string;
  untilDate?: string;
  count?: string;
};

type RecurrenceFormValues = {
  startDate: string;
  recurringRule?: RecurrenceRuleValues;
};

export const getFluentIdForSECCode = (secCode: API.ACHPayment.SECCode) => {
  switch (secCode) {
    case "CCD":
      return {
        id: "ccd-payment-description",
        vars: {},
        fallback:
          "A CCD entry can be either a buyer-initiated or seller-initiated transaction used to move funds between the buyer’s and seller’s financial institution accounts. It is also used by companies to move funds from outlying depository locations to a central bank account. A CCD entry supports including one addenda and is typically used when paying for one item.",
      } as const;
    case "CIE":
      return {
        id: "cie-payment-description",
        vars: {},
        fallback:
          "A CIE entry is a credit entry initiated on behalf of, and upon the instruction of, a consumer to transfer funds to a non-consumer Receiver.",
      } as const;
    case "CTX":
      return {
        id: "ctx-payment-description",
        vars: {},
        fallback:
          "A CTX entry is similar to a CCD entry, but it supports including multiple addenda.",
      } as const;
    case "IAT":
      return {
        id: "iat-payment-description",
        vars: {},
        fallback:
          "An IAT entry is a debit or credit entry that is part of a payment transaction involving a financial agency’s office that is not located in the territorial jurisdiction of the United States.",
      } as const;
    case "PPD":
      return {
        id: "ppd-payment-description",
        vars: {},
        fallback:
          "A PPD entry is a credit or debit entry originated by an organization to a consumer’s account, based on standing or single-entry authorization from that consumer. A PPD entry supports including one addenda.",
      } as const;
    case "WEB":
      return {
        id: "web-payment-description",
        vars: {},
        fallback:
          "WEB entries are entries transmitted to a consumer Receiver’s account. These entries can be either debits or credits. WEB entries may be either recurring or non-recurring transactions.",
      } as const;
    default:
      return undefined;
  }
};

// takes form values and spits out the values to be used by generateRecurrenceRuleString
export const formatValuesForRecurrenceString = (
  values: RecurrenceFormValues,
) => {
  const { recurringRule } = values;

  let formattedStartDate: string;

  if (values.startDate.includes("-")) {
    formattedStartDate = values.startDate;
  } else {
    formattedStartDate = DateTime.fromFormat(
      values.startDate,
      "M/d/yyyy",
    ).toFormat("yyyy-MM-dd");
  }

  const formattedTodayDate = DateTime.now().toFormat("yyyy-MM-dd");

  let formattedUntilDate;
  if (recurringRule?.ends === "on" && recurringRule.untilDate) {
    if (recurringRule.untilDate.includes("-")) {
      // format from mobile comes back as YYYY-MM-DD
      formattedUntilDate = recurringRule.untilDate;
    } else {
      formattedUntilDate = DateTime.fromFormat(
        recurringRule.untilDate,
        "M/d/yyyy",
      ).toFormat("yyyy-MM-dd");
    }
  }

  const count =
    recurringRule?.ends === "after" && Number(recurringRule.count) > 0
      ? recurringRule.count
      : null;

  const getFrequency = () => {
    const isToday = formattedStartDate === formattedTodayDate;
    if (recurringRule?.isVisible && recurringRule.frequency) {
      return recurringRule.frequency;
    }
    // if recurringRule is not visible, and it is sent on a future date,
    // set frequency as "ONCE"
    return !isToday ? FREQUENCIES.ONCE : "";
  };

  const frequency = getFrequency();

  return {
    frequency,
    startDate: formattedStartDate,
    untilDate: formattedUntilDate,
    count,
  };
};

// startDate arrives as either "yyyy-MM-dd" (set by useInvoicePrefill on the
// details screen) or "MM/dd/yyyy" (set by getStartDate below) - native Date
// parsing of the latter is engine-dependent (unreliable on Hermes), so parse
// explicitly rather than handing the raw string to filters.americanDate
export const parseACHStartDate = (value: string) =>
  value.includes("-")
    ? parse(value, "yyyy-MM-dd", new Date())
    : parse(value, "MM/dd/yyyy", new Date());

// same-day and instant both submit against today regardless of the date shown on
// the caller's screen (see usePinnedACHStartDate) rather than a member-chosen
// future date - this is the single place that's decided, so anything that needs
// to know (pinning the date, disabling a date field bound to the shared form,
// choosing copy) applies it consistently instead of re-deriving it inline
export const isExpeditedACHDeliverySpeed = (speed: ACHDeliverySpeed): boolean =>
  speed === "sameDay" || speed === "instant";

// achNextCutoff is Unix timestamp (in seconds)
export const getStartDate = (achNextCutoff?: number) => {
  // Date.now() gives milliseconds. therefore we have to divide by 1000 to turn it into seconds like a Unix timestamp
  const timestamp = achNextCutoff || Math.floor(Date.now() / 1000);

  const dt = DateTime.fromSeconds(timestamp).setZone("local"); // shift into user's local timezone

  const formattedDate = dt.toFormat("MM/dd/yyyy");

  return formattedDate;
};

// achNextCutoff is Unix timestamp (in seconds).
// returns true when today's ACH cut-off time has already passed, i.e. the next
// available ACH date (getStartDate) is a future day rather than today.
export const getIsPastACHCutoff = (achNextCutoff?: number) => {
  if (!achNextCutoff) {
    return false;
  }

  // getStartDate already encodes the next available ACH date (with timezone
  // handling); the cutoff has passed when that date is not today
  return getStartDate(achNextCutoff) !== getStartDate();
};

// whether `date` is a business day (not a weekend or a holiday).
// holidayDates are "yyyy-MM-dd" strings
export const getIsBusinessDay = (date: DateTime, holidayDates: string[] = []) =>
  // luxon weekday: 6 = Saturday, 7 = Sunday
  ![6, 7].includes(date.weekday) &&
  !holidayDates.includes(date.toFormat("yyyy-MM-dd"));

// returns `from` if it is already a business day, otherwise the next business
// day after it (skipping weekends and holidays)
export const getNextBusinessDay = (
  from: DateTime,
  holidayDates: string[] = [],
) => {
  let result = from;
  while (!getIsBusinessDay(result, holidayDates)) {
    result = result.plus({ days: 1 });
  }
  return result;
};

// moves `from` date forward by `businessDays` (skipping weekends and holidays)
// returns a DateTime
export const addBusinessDays = (
  from: DateTime,
  businessDays: number,
  holidayDates: string[] = [],
) => {
  let result = from;
  let remaining = businessDays;
  while (remaining > 0) {
    result = result.plus({ days: 1 });
    if (getIsBusinessDay(result, holidayDates)) {
      remaining -= 1;
    }
  }
  return result;
};

// achNextCutoff is Unix timestamp (in seconds)
export const getReadableTimeFromCutoff = (achNextCutoff?: number) => {
  if (!achNextCutoff) {
    return "";
  }

  const userZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const dt = DateTime.fromSeconds(achNextCutoff, { zone: userZone });

  // hour + AM/PM ("8PM")
  const hourPart = dt.toFormat("ha");

  // get timezone abbreviation ("EST")
  const tzNamePart =
    new Intl.DateTimeFormat("en-US", {
      timeZone: userZone,
      timeZoneName: "short",
      hour: "numeric",
    })
      .formatToParts(dt.toJSDate())
      .find((p) => p.type === "timeZoneName")?.value ?? "";

  // combine ("8PM EST")
  return `${hourPart} ${tzNamePart}`;
};

export type InvoicePayRecommendedSpeed = "standard" | "sameDay" | "instant";

type InvoicePaySpeedRecommendationProps = {
  dueDate?: string | null;
  holidayDates?: string[];
  isPastCutoff: boolean;
  hasSameDayOption: boolean;
  hasInstantOption: boolean;
  now?: DateTime;
};

const getFirstAvailableInvoicePaySpeed = (
  priorities: InvoicePayRecommendedSpeed[],
  {
    hasSameDayOption,
    hasInstantOption,
  }: Pick<
    InvoicePaySpeedRecommendationProps,
    "hasSameDayOption" | "hasInstantOption"
  >,
): InvoicePayRecommendedSpeed =>
  priorities.find((speed) => {
    if (speed === "standard") {
      return true;
    }

    if (speed === "sameDay") {
      return hasSameDayOption;
    }

    return hasInstantOption;
  }) ?? "standard";

const parseInvoicePayDueDate = (dueDate?: string | null) => {
  if (!dueDate) {
    return null;
  }

  return ["MM/dd/yyyy", "M/d/yyyy", "yyyy-MM-dd"]
    .map((format) => DateTime.fromFormat(dueDate, format))
    .find((parsedDate) => parsedDate.isValid);
};

export const getRecommendedInvoicePaySpeed = ({
  dueDate,
  holidayDates = [],
  isPastCutoff,
  hasSameDayOption,
  hasInstantOption,
  now = DateTime.now(),
}: InvoicePaySpeedRecommendationProps):
  | InvoicePayRecommendedSpeed
  | undefined => {
  const availableOptionCount =
    1 + Number(hasSameDayOption) + Number(hasInstantOption);

  if (availableOptionCount <= 1) {
    return undefined;
  }

  const parsedDueDate = parseInvoicePayDueDate(dueDate)?.startOf("day");
  if (!parsedDueDate?.isValid) {
    return "standard";
  }

  const today = now.startOf("day");
  const nextBusinessDay = addBusinessDays(today, 1, holidayDates).startOf(
    "day",
  );

  if (parsedDueDate < today) {
    return getFirstAvailableInvoicePaySpeed(
      ["instant", "sameDay", "standard"],
      { hasSameDayOption, hasInstantOption },
    );
  }

  if (parsedDueDate.hasSame(today, "day")) {
    return getFirstAvailableInvoicePaySpeed(
      isPastCutoff
        ? ["instant", "sameDay", "standard"]
        : ["sameDay", "instant", "standard"],
      { hasSameDayOption, hasInstantOption },
    );
  }

  if (isPastCutoff && parsedDueDate.hasSame(nextBusinessDay, "day")) {
    return getFirstAvailableInvoicePaySpeed(
      ["sameDay", "instant", "standard"],
      { hasSameDayOption, hasInstantOption },
    );
  }

  return "standard";
};

export const getIsOneTimePaymentForToday = (
  values: RecurrenceFormValues,
  _achNextCutoff?: number,
) => {
  // start date can be 2 different formats
  const parseDate = (dateString: string) => {
    const formats = ["MM/dd/yyyy", "yyyy-MM-dd"];
    return (
      formats
        .map((format) => DateTime.fromFormat(dateString, format))
        .find((parsed) => parsed.isValid) || null
    );
  };

  const formattedStartDate = parseDate(values.startDate);
  const todayDate = DateTime.now();
  const isStartDateToday = Boolean(
    formattedStartDate?.hasSame(todayDate, "day"),
  );

  return !values.recurringRule?.isVisible && isStartDateToday;
};

export const getAmountAvailableByTransactionTypeAsFloat = (
  limits: LimitsForDisplayType,
) => {
  return Object.keys(limits).reduce(
    (acc: { [key: string]: number }, transactionType) => {
      if (
        // only care about ACH-related (and fednow) limits
        !["immediate_rdc", "wire", "organization_user_wire"].includes(
          transactionType,
        )
      ) {
        // limits[transaction] has numbers (which represent days) and "amount_available" as keys
        // amount_available key is relevant for validation
        const amountAvailable = limits[transactionType].amount_available;
        if (amountAvailable) {
          acc[transactionType] = parseFloat(amountAvailable);
        }
      }
      return acc;
    },
    {},
  );
};

export const getScreenWithError = (errors: any, defaultError: string) => {
  if (!errors || typeof errors !== "object") {
    return {
      screen: 1,
      message: defaultError,
    };
  }

  const fieldsOnFirstScreen = ["recipient", "transaction_type", "ach_company"];

  const fieldsOnSecondScreen = ["institution_account", "amount"];

  const fieldsOnThirdScreen = ["std_ent_cls_code", "entry_desc"];

  const findFieldError = (
    errorsResponse: any,
    fields: string[],
    screen: number,
  ) => {
    const field = fields.find((f) => errorsResponse?.[f]?.length);
    return field ? { screen, field, message: errorsResponse[field][0] } : null;
  };

  // first screen error
  const firstScreenError = findFieldError(errors, fieldsOnFirstScreen, 1);
  if (firstScreenError) return firstScreenError;

  // second screen error
  const secondScreenError = findFieldError(errors, fieldsOnSecondScreen, 2);
  if (secondScreenError) return secondScreenError;

  // third screen error
  const thirdScreenError = findFieldError(errors, fieldsOnThirdScreen, 3);
  if (thirdScreenError) return thirdScreenError;

  // fallback to non_field_errors or string message and default to screen 1
  const fallbackMessage =
    errors.message?.non_field_errors?.[0] ||
    (typeof errors.message === "string" ? errors.message : defaultError);

  return { screen: 1, message: fallbackMessage };
};
